<template>
  <div class="content">
    <card class="text-left">
      <template slot="header">
        <h4 class="card-title">기본 템플릿 · 편집</h4>
        <p class="card-category">
          본문을 작성하지 않은 모든 투표가 상세에서 노출하는 <b>공통 기본 템플릿</b>입니다.<br />
          {@아티스트홈명}·{@팬덤명}·{@스케줄명}·{@펀딩종료일시}·{@펀딩종료일시+5} 는 투표별로 자동 치환됩니다.<br />
          여기서 저장하면 개별 투표상세에서 본문을 따로 작성하지 않은 모든 투표에 바로 반영됩니다
          <br />(개별 투표에 본문을 따로 작성해둔 경우에는 그 내용이 우선 노출됩니다). <br />
          이미지는 각 에디터에 직접 업로드해 반영합니다.
        </p></template
      >

      <div class="row mb-2">
        <div class="col-md-4">
          <label class="col-form-label">투표 타입</label>
          <el-select v-model="activePollType" style="width: 100%" @change="onTypeChange">
            <el-option v-for="t in PollTypeOptions" :key="t.value" :label="t.label" :value="t.value" />
          </el-select>
        </div>
      </div>

      <div class="row">
        <div class="col-sm-12">
          <label class="col-form-label">안내 (notice)</label>
          <blip-i18n-editor v-model="notice" :tokens="true" :image="true" :upload-image="uploadImageFn" placeholder="기본 안내 본문을 입력하세요" />
          <div class="mt-2 d-flex align-items-center">
            <base-button @click="saveBody('notice')" type="primary" :disabled="loading">안내 저장</base-button>
            <span class="ml-2 badge badge-pill" :class="noticeDirty ? 'badge-warning' : 'badge-success'">
              {{ noticeDirty ? "작업 중 · 미저장" : "저장됨" }}
            </span>
          </div>
        </div>
      </div>

      <hr />

      <div class="row">
        <div class="col-sm-12">
          <label class="col-form-label">리워드 가이드 (guide)</label>
          <blip-i18n-editor v-model="guide" :tokens="true" :image="true" :upload-image="uploadImageFn" placeholder="기본 리워드 가이드 본문을 입력하세요" />
          <div class="mt-2 d-flex align-items-center">
            <base-button @click="saveBody('guide')" type="primary" :disabled="loading">가이드 저장</base-button>
            <span class="ml-2 badge badge-pill" :class="guideDirty ? 'badge-warning' : 'badge-success'">
              {{ guideDirty ? "작업 중 · 미저장" : "저장됨" }}
            </span>
          </div>
        </div>
      </div>
    </card>
  </div>
</template>

<script>
import axios from "axios";
import { BlipI18nEditor } from "@space-oddity-inc/editor-vue2";
import "@space-oddity-inc/editor-vue2/dist/style.css";
import { Card, BaseButton } from "@/components/index";
import { BlipFileUploader, UploadCategory } from "@/util/BlipFileUploader";
import { PollType, PollTypeOptions } from "@/common/PollTypes";

const LANGS = ["ko", "en", "ja"];

// 빈 본문(빈 <p> 등) 은 서버 검증(html 비어있으면 안 됨)을 통과하지 못하므로 저장 대상에서 제외한다.
function isBlankHtml(html) {
  if (!html) return true;
  const text = html
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .trim();
  if (text.length > 0) return false;
  return !/<img\b/i.test(html);
}

// 에디터가 넘긴 File 의 자연 크기 (uploader requestUploadUrl 의 width/height 용).
function readImageSize(file) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve({ width: 0, height: 0 });
    };
    img.src = url;
  });
}

// 저장될 본문(로케일 맵)이 실제 참조하는 업로드 이미지의 fileId 집합을 추출한다.
function collectUploadedFileIds(htmlMap) {
  const prefix = window.Endpoints.imageServerUrl;
  const escaped = prefix.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const re = new RegExp(escaped + "([0-9a-zA-Z]+)", "g");
  const ids = new Set();
  Object.values(htmlMap || {}).forEach((html) => {
    if (!html) return;
    let m;
    while ((m = re.exec(html)) !== null) ids.add(m[1]);
  });
  return ids;
}

export default {
  components: {
    Card,
    BaseButton,
    "blip-i18n-editor": BlipI18nEditor,
  },
  data() {
    return {
      PollType,
      PollTypeOptions,
      activePollType: PollType.FUNDING,
      notice: {},
      guide: {},
      savedNotice: "{}",
      savedGuide: "{}",
      loading: false,
    };
  },
  computed: {
    pollTypeLabel() {
      return this.activePollType === PollType.RANKING ? "경쟁 투표" : "서포트";
    },
    noticeDirty() {
      return JSON.stringify(this.notice) !== this.savedNotice;
    },
    guideDirty() {
      return JSON.stringify(this.guide) !== this.savedGuide;
    },
    // 에디터(자식)가 호출할 때 this 바인딩이 유지되도록 화살표로 감싸 prop 으로 전달.
    uploadImageFn() {
      return (file) => this.uploadImage(file);
    },
  },
  async created() {
    await this.getDefaultBody();
  },
  methods: {
    // 저장하지 않은 변경사항이 있는 채로 타입을 전환하면 확인을 받고, 취소 시 이전 타입으로 되돌린다.
    async onTypeChange(newType) {
      const prevType = newType === PollType.RANKING ? PollType.FUNDING : PollType.RANKING;
      if (this.noticeDirty || this.guideDirty) {
        if (!window.confirm("저장하지 않은 변경사항이 있습니다. 타입을 전환하면 사라집니다. 계속할까요?")) {
          this.activePollType = prevType;
          return;
        }
      }
      await this.getDefaultBody();
    },
    // field 미지정 시 둘 다 로드(최초 진입). field 지정 시 해당 본문만 재동기화(저장 직후 반대편 미저장분 유실 방지).
    async getDefaultBody(field) {
      this.loading = true;
      try {
        const res = await this.$axios.get(`/votes/default-body`, { params: { type: this.activePollType } });
        const data = res.data.data || {};
        if (!field || field === "notice") {
          this.notice = data.notice || {};
          this.savedNotice = JSON.stringify(this.notice);
        }
        if (!field || field === "guide") {
          this.guide = data.guide || {};
          this.savedGuide = JSON.stringify(this.guide);
        }
      } catch (err) {
        // 미시드(빈 객체) 등은 치명적이지 않음 — 빈 상태 유지
      } finally {
        this.loading = false;
      }
    },
    async saveBody(field) {
      const map = this[field] || {};
      const langs = LANGS.filter((lang) => !isBlankHtml(map[lang]));
      if (langs.length === 0) {
        window.Toast.error("최소 한 개 로케일의 본문을 입력해주세요");
        return;
      }
      try {
        for (const lang of langs) {
          await this.$axios.put(`/votes/default-body`, { params: { field, lang, html: map[lang] } }, { params: { type: this.activePollType } });
        }
        // GC-ready: 저장된 본문이 실제 참조하는 업로드 이미지만 state=1 로 확정한다(베스트에포트).
        await this.confirmReferencedImages(map);
        window.Toast.success(`기본 ${field === "notice" ? "안내" : "가이드"} 템플릿이 저장되었습니다`);
        await this.getDefaultBody(field);
      } catch (err) {
        window.Toast.error(err.response?.data?.error?.message ?? "기본 템플릿 저장 중 에러가 발생하였습니다");
      }
    },
    // 저장 직후 호출. 본문이 참조하는 fileId 만 확정(state=1) → 미참조/미저장 업로드는 state=0(고아) 유지. 실패는 삼킨다.
    async confirmReferencedImages(htmlMap) {
      const ids = collectUploadedFileIds(htmlMap);
      if (ids.size === 0) return;
      const uploader = new BlipFileUploader();
      for (const fileId of ids) {
        try {
          await uploader.markFileAsUploaded(fileId);
        } catch (e) {
          console.warn("[funding-default] 이미지 확정(state=1) 실패", fileId, e);
        }
      }
    },
    async uploadImage(file) {
      const uploader = new BlipFileUploader();
      const { width, height } = await readImageSize(file);
      const result = await uploader.requestUploadUrl({
        category: UploadCategory.DASHBOARD,
        mimeType: file.type,
        fileName: file.name,
        width,
        height,
        fileSize: file.size,
      });
      const form = new FormData();
      Object.entries(result.fields).forEach(([key, value]) => form.append(key, value));
      form.append("file", file);
      // S3 presigned POST — 앱 axios($axios) 가 아닌 bare axios 로 보내 쿠키·기본 헤더를 싣지 않는다.
      await axios.post(result.uploadPath, form, { withCredentials: false });
      // GC-ready: 업로드 즉시 확정하지 않고 본문 저장 시 참조분만 확정.
      return window.Endpoints.imageServerUrl + result.fileId;
    },
  },
};
</script>
